import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import './index.css';
import './marketplace.css';
import { CartProvider } from './CartContext';
import { LanguageProvider } from './i18n';
import FriendInviteGate from './components/FriendInviteGate';
import RouteSeo from './components/RouteSeo';
import reportWebVitals from './reportWebVitals';
import { trackEvent } from './utils/analytics';

// Load only the route the customer opens. The landing page no longer downloads
// checkout/account code, and marketplace visitors do not download the animated
// landing-page scenes and their images.
const App = lazy(() => import('./pages/HomePage'));
const PrivacyPolicyPage = lazy(() => import('./PrivacyPolicyPage'));
const SongWatGuide = lazy(() => import('./pages/SongWatGuide'));
const SongWatShop = lazy(() => import('./pages/SongWatShop'));
const BrandPage = lazy(() => import('./pages/BrandPage'));
const BrowniePage = lazy(() => import('./pages/BrowniePage'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const LineCallback = lazy(() => import('./pages/LineCallback'));
const Account = lazy(() => import('./pages/Account'));

// Every navigation starts at the top of the page (React Router otherwise
// keeps the previous scroll position — e.g. clicking สั่งเลย from the footer
// would land you mid-marketplace).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent('page_view', {}, { dedupe: true });
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <span className="route-loading-spinner" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <LanguageProvider>
        <CartProvider>
        <ScrollToTop />
        <RouteSeo />
        <FriendInviteGate />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/songwat-guide" element={<SongWatGuide />} />
            <Route path="/songwat-brownie-shop" element={<SongWatShop />} />
            <Route path="/about-browmie" element={<BrandPage />} />
            <Route path="/brownie" element={<BrowniePage />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order/success" element={<OrderSuccess />} />
            <Route path="/line-callback" element={<LineCallback />} />
            <Route path="/account" element={<Account />} />
            <Route path="/rewards" element={<Account rewardsOnly />} />
          </Routes>
        </Suspense>
        </CartProvider>
      </LanguageProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
